import { useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { Button, LoadingDots } from '@corratech/pylot-ui'
import { getIsValidEmailAddress } from '@corratech/pylot-utils'
import cn from 'classnames'
import s from '@pagestyles/Blog.module.scss'
import { useRouter } from 'next/router'
import formStyles from '@commonStyles/Forms.module.scss'
import { FC, useEffect, useState } from 'react'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { BLOG_COMMENT } from '@pylot-data/hooks/blog/graphql/blogQuery'
import { CommentInputVariable } from '@pylot-data/api/operations/save-comment'
import BlogComment from '@components/blog/Comment'
import { IBlogComment } from '@pylot-data/api/operations/get-blogcomments'

export interface CommentFormInput {
    post_id?: string
    text: string
    author_email: string
    author_nickname: string
    parent_id?: number
}

export interface CommentFormProps {
    post_id?: string
}

const BlogCommentForm: FC<CommentFormProps> = (props: CommentFormProps) => {
    const { t } = useTranslation(['blog'])
    const { openToast } = useUI()
    const {
        register: commentRegister,
        handleSubmit: handleCommentFormSubmit,
        reset: commentFormReset,
        formState: { errors: errorsComment }
    } = useForm<CommentFormInput>({ mode: 'onSubmit' })
    const RequriedFieldMessage = t('blog||This field is required')
    const { locale } = useRouter()
    const [isLoading, setIsLoading] = useState(false)
    const [postId, setPostId] = useState<string | undefined>(undefined)

    const saveBolgComment = async (formInput: CommentFormInput) => {
        setIsLoading(true)

        const response = await graphqlFetch<CommentInputVariable, IBlogComment>(
            {
                query: BLOG_COMMENT,
                variables: {
                    author_email: formInput.author_email,
                    author_nickname: formInput.author_nickname,
                    text: formInput.text,
                    post_id: props.post_id,
                    parent_id: formInput.parent_id || 0
                },
                locale
            }
        )

        setIsLoading(false)
        return response
    }

    const onSubmitCommentForm = async (inputs: CommentFormInput) => {
        try {
            const response = await saveBolgComment(inputs)

            //handle errors
            if (response?.errors?.length) {
                openToast(t(response?.errors[0]?.message), ToastType.Danger)
            }

            if (
                response.data.addCommentToPost &&
                response.data.addCommentToPost.comments
            ) {
                openToast(t('Comment added successfully!'), ToastType.Success)
                //reset form if success
                commentFormReset()
                setIsLoading(false)
            }
        } catch (e) {
            if (e) {
                setIsLoading(false)
                openToast(
                    t('Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    useEffect(() => {
        setPostId(props.post_id)
    }, [props.post_id])

    return (
        <>
            <div className={s['commentsform']}>
                <div className={s['commentsinput']}>
                    <section className={formStyles.root}>
                        <form
                            key="couponForm"
                            onSubmit={handleCommentFormSubmit(
                                onSubmitCommentForm
                            )}
                        >
                            <h2>{t('Add Comment')}</h2>
                            <div className="global-container">
                                <div className="flex w-full comment-form">
                                    <div
                                        className="flex-initial form-grid-col form-group"
                                        key="author_nickname"
                                    >
                                        <input
                                            id="author_nickname"
                                            type="text"
                                            placeholder={t('blog||Name')}
                                            {...commentRegister(
                                                'author_nickname',
                                                {
                                                    required: RequriedFieldMessage
                                                }
                                            )}
                                            className={cn('form-input', {
                                                'border-alert-danger':
                                                    errorsComment.author_nickname
                                            })}
                                        />
                                        {errorsComment.author_nickname && (
                                            <span className="error">
                                                {t(
                                                    `${errorsComment.author_nickname.message}`
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className="flex-initial form-grid-col form-group"
                                        key="author_email"
                                    >
                                        <input
                                            type="email"
                                            {...commentRegister(
                                                'author_email',
                                                {
                                                    required: RequriedFieldMessage,
                                                    validate: getIsValidEmailAddress
                                                }
                                            )}
                                            placeholder={t('blog||Email')}
                                            className={cn('form-input', {
                                                'border-alert-danger':
                                                    errorsComment.author_email
                                            })}
                                            id="author_email"
                                        />
                                        {errorsComment.author_email && (
                                            <span className="error">
                                                {t(
                                                    `${errorsComment.author_email.message}`
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex w-full">
                                    <div
                                        className="w-full form-group flex-col"
                                        key="text"
                                    >
                                        <textarea
                                            id="text"
                                            placeholder={t('blog||Comment')}
                                            {...commentRegister('text', {
                                                required: RequriedFieldMessage
                                            })}
                                            className={cn('form-input', {
                                                'border-alert-danger':
                                                    errorsComment.text
                                            })}
                                        />
                                        {errorsComment.text && (
                                            <span className="error">
                                                {t(
                                                    `${errorsComment.text.message}`
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <Button type="submit" disabled={isLoading}>
                                {t('Submit')}
                                {isLoading && <LoadingDots />}
                            </Button>
                        </form>
                    </section>
                </div>
            </div>
            <BlogComment post_id={postId ? postId : undefined} />
        </>
    )
}

export default BlogCommentForm
